import { i18n } from '../i18n.js';
/**
 * TouchPreview — tap-to-enlarge card inspection for touch devices.
 *
 * The desktop preview systems (card-hover, crisp-hover, the seven catalog
 * #card-hover-preview forks) are mouse-driven. Rather than reimplementing
 * them, a TAP dispatches a SYNTHETIC mouseenter on the tapped element —
 * driving every existing preview unmodified — and the preview stays LATCHED
 * open with no finger on the glass. Synthetic events carry
 * isTrusted === false, which is exactly how preview systems distinguish them
 * from the compatibility mouse events mobile browsers synthesize after taps
 * (those are trusted and are ignored while isSuppressingMouse() holds).
 *
 * The gesture is a latch, not a hold, because a hold is the wrong shape for
 * inspection: the finger has to stay on the very card it is revealing, so it
 * covers the text being read, and letting go to read closes it.
 *
 *   1st tap on a card ......... enlarge (trailing click swallowed)
 *   2nd tap on that card ...... act on it (play / add / select)
 *   tap the enlarged popup .... act on it — the popup usually covers its source
 *   tap another card .......... enlarge that one instead
 *   tap anywhere else ......... dismiss, and ONLY dismiss (click swallowed, so
 *                               closing a preview can never also press a button)
 *   pinch (a second finger) ... never a tap: no press, no act, no dismiss. A
 *                               pinch on the enlarged card is the in-app
 *                               magnifier (zoom-policy.js scales the popup and
 *                               springs it back on release), so the latch
 *                               holds through the whole gesture and through a
 *                               pointercancel an engine may send mid-pinch.
 *
 * Hover is untouched: a mouse on a real pointer device never enters this
 * module (the exception is an explicit mobile-UI emulator, which is a phone
 * stand-in and gets phone semantics on purpose).
 *
 * Callback mode: attach(el, { onPreview, onDismiss }) fires the callbacks
 * instead of dispatching synthetic mouse events — for surfaces whose mouse
 * preview is not mouseenter-driven (the in-game hand/board popup preview).
 * Click suppression and every cancellation rule are identical in both modes.
 *
 * The enlarging tap is answered before its enlargement is: the pressed card
 * wears touch-press.js's pending mark until the preview shows (see
 * startPressAck).
 *
 * Contract: tests/unit/ui/touchPreview.test.js,
 * tests/unit/ui/touchPreviewPressAck.test.js.
 */
import { CardDrag } from './card-drag.js';
import { Platform } from './platform.js';
import { TouchPress } from './touch-press.js';
let TouchPreview = {};

(function () {
  /* A second evaluation of this file (a duplicated script tag, a hot reload, a
     test re-requiring the module) would leave the previous instance listening
     on document and possibly holding an armed click swallow — two live latches
     answering the same tap. Retire the old one first. */
  // Kept off window.UI on purpose: that namespace is re-created wholesale in
  // places (and by the test harness), which would orphan the previous instance's
  // document listeners and leave two live latches answering the same tap.
  try { window.__moonfallTouchPreview?.destroy?.(); } catch (_) { /* nothing to retire */ }

  /* The tap and the drag must split the gesture at the SAME distance. If the
     tap gave up first, movement in the gap would cancel the inspection without
     starting a drag and the gesture would do nothing at all. CardDrag owns the
     number; this fallback only covers a page where it has not loaded. */
  const FALLBACK_SLOP_PX = 12;
  const MOUSE_SUPPRESS_MS = 700;

  function slopPx() {
    try {
      const published = CardDrag?.touchSlopPx?.();
      return Number.isFinite(published) ? published : FALLBACK_SLOP_PX;
    } catch (_) {
      return FALLBACK_SLOP_PX;
    }
  }

  /* Every popup-preview surface in the app. A tap landing inside one of these
     is a tap on the enlarged card itself, which normally sits OVER its own
     source — so it must act on the source, not fall on the floor. Forks that
     set pointer-events:none never match here: their taps hit the source card
     directly and take the same-element path instead. */
  /* Surfaces where a stray click COSTS something: playing a card, adding it to
     a deck, spending a mulligan. A dismissing tap must not do any of those by
     accident — but it must not swallow an ordinary button press either. */
  const PLAYABLE_SURFACES = [
    '.card',
    '.deck-card-item',
    '.mulligan-card-wrapper',
    '.card-pack-card',
    '.sidebar-element',
    '.element',
  ].join(', ');

  const PREVIEW_SURFACES = [
    '#card-hover-preview',
    '.card-hover-preview',
    '#mulligan-card-hover-preview',
  ].join(', ');

  /* The declared-action control is the one surface a tap-away must NOT treat
     as "somewhere else". It lives outside the card by design — it must never
     cover the card it is offering to act on — so the generic dismissal would
     fire on its own press, hide it, and leave the click with nothing to land
     on. The button's click handler performs the dismissal itself. */
  /**
   * What the control must not cover.
   *
   * The ENLARGED card when one is on screen, not the small source — and
   * specifically the CARD, not the popup surface that hosts it. The surface is
   * a positioning container that can be far larger than the card inside it, so
   * anchoring to the surface left every candidate placement overlapping it,
   * the least-overlap fallback took over, and the control landed on the card
   * anyway. The player is reading that card in order to decide.
   */
  function actionAnchorFor(el) {
    const surface = document.querySelector(PREVIEW_SURFACES);
    return surface?.querySelector?.('.card') || surface || el;
  }

  function insideActionControl(target) {
    return !!(target && typeof target.closest === 'function'
      && target.closest('#touch-preview-action'));
  }

  /* Frames of unchanged anchor geometry before the control is shown. Three is
     ~50ms — long enough to outlast the frame-to-frame jitter of a transition,
     short enough that the control does not feel late. */
  const ANCHOR_SETTLE_FRAMES = 3;

  /* ...but settling is a NICETY, and the button existing is the feature.
     Waiting for stillness assumed the motion ends. A card with an idle float,
     a pulse, or a tilt that tracks the pointer never produces three identical
     frames, so the control stayed hidden for the whole latch — reported as
     "the mulligan no longer had that yellow button ... it should remain".
     Past this deadline the control appears wherever the card currently is.

     Measured from the moment the ANCHOR last changed identity, not from the
     latch opening. The enlargement arrives partway through the latch, and a
     deadline counted from the start had already expired by then — so the
     control was revealed on the popup's very first frame and then danced
     through the whole growth transition, which is the thing this gate exists
     to prevent. */
  const ACTION_REVEAL_DEADLINE_MS = 400;

  /**
   * How long the control waits for the enlargement it is meant to sit beside.
   *
   * `actionAnchorFor` falls back to the SOURCE element while no popup exists,
   * and every catalog mounts its popup asynchronously — it awaits a card
   * raster first, which on a phone at DPR 3 costs a few hundred milliseconds.
   * Revealing during that window seats the control against the small grid
   * card, inside the dense grid, ON TOP of a neighbouring card; the control
   * then teleports when the popup lands. Measured on the real deck builder at
   * a 915x336 page box with a 250ms raster: revealed at 238ms at x=300 over
   * the pool card `stellar:aquaflop`, gone to x=362 by 515ms. A thumb already
   * moving toward x=300 arrives after the control has left, lands on that pool
   * card, and TouchPreview latches it — so the press is spent enlarging a card
   * the player did not ask for. Reported from Android: "instead of pressing
   * it, it changes the highlight from the card that I'm viewing to the card
   * that is beneath that button."
   *
   * It is a GRACE and not a gate: a readiness probe may never delete the
   * control (the mulligan lost its yellow button to exactly that once). Past
   * this the control is seated against its source, precisely as before.
   */
  const ENLARGEMENT_GRACE_MS = 1200;

  /* How far the anchor must actually move before the control is repositioned,
     once it is already on screen. Chasing every changed pixel is what made it
     "bounce randomly"; below this, the move is not worth the distraction. */
  const ACTION_REANCHOR_THRESHOLD_PX = 12;

  /* THE ENLARGING TAP IS ANSWERED BEFORE ITS ENLARGEMENT IS.
     Every surface shows its preview only once the card's raster has settled,
     and on the phone seats that took 112-356 ms after the touch (measured 25
     September 2026: Collection tiles, the battle hand), with nothing on screen
     in between; every press has to be answered within 100 ms. The finger lifts
     at ~100 ms, so the answer has to start while it is still down.

     So the pressed card is held by touch-press.js's pending mark from the
     touch (drawn only after that module's 60 ms grace, as a ring outside the
     card's frame: never a transform, the card's raster is not touched) until
     its answer shows: a fresh enlarged card on a preview surface, the card
     growing in place, or a disclosure opening. A press that becomes a drag,
     a pinch or a scroll, a tap away and a card that leaves all drop it; so
     does the cap, for a surface whose answer never comes. */
  const PRESS_ACK_CAP_MS = 6000;
  // In-place enlargements grow a card 1.6x and more; a sticky touch :hover
  // lift (scale 1.05) must not read as one.
  const IN_PLACE_GROWTH_AREA = 1.5;
  let pendingAck = null;      // { el, target, pointerId, phase, release, area, startedAt, stale, expanded, frame }

  const attached = new WeakSet();
  const attachedOptions = new WeakMap();

  const touchPointers = new Set(); // pointerIds of every touch currently down
  let pinching = false;       // a second finger joined; true until every finger lifts
  let suppressUntil = 0;      // compatibility-mouse suppression deadline
  let press = null;           // { el, callbacks, pointerId, startX, startY, moved }
  let latched = null;         // { el, callbacks } — preview held open, no finger down
  let pendingDragDismissal = null;
  let armedSwallow = null;    // { el, fn, timer } — the one-shot trailing-click swallow
  let latchedAction = null;   // { label, run } while a declared action is offered
  let inspectionDiscovered = false;

  /* Resolve a surface's own second-tap promise. There is deliberately no
     default: a hint that is not true of the surface showing it is worse than
     silence, and the generic "Tap again to play" was painted over board cards
     that act by dragging, targetable Spells, storyboard tiles and revealed
     enemy spells alike. */
  function latchedHintText(hint) {
    const fallback = hint?.fallback || '';
    try {
      const key = hint?.key;
      if (!key) return fallback;
      const translated = i18n?.t?.(key);
      return translated && translated !== key ? translated : fallback;
    } catch (_) {
      return fallback;
    }
  }

  function instructionText() {
    try {
      const key = 'game.tapToEnlarge';
      const translated = i18n?.t?.(key);
      return translated && translated !== key ? translated : 'Tap to enlarge';
    } catch (_) {
      return 'Tap to enlarge';
    }
  }

  function ensureInstructions() {
    let instructions = document.getElementById('touch-preview-instructions');
    if (!instructions) {
      instructions = document.createElement('span');
      instructions.id = 'touch-preview-instructions';
      instructions.className = 'touch-preview-instructions';
      instructions.dataset.i18n = 'game.tapToEnlarge';
      document.body.appendChild(instructions);
    }
    instructions.textContent = instructionText();
    return instructions;
  }

  function describeInspection(el) {
    ensureInstructions();
    const describedBy = new Set(
      String(el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
    );
    describedBy.add('touch-preview-instructions');
    el.setAttribute('aria-describedby', [...describedBy].join(' '));
  }

  /* ---- the ACT half of the gesture ---------------------------------------
     Inspecting and acting are different things, so they get different
     gestures. A surface that declares an `action` opts its cards out of
     "tap again to act" entirely: every tap inspects, and acting means pressing
     a labelled control on the enlarged card. The label is resolved per latch so
     it can follow the card's own state ("Add to deck" / "Remove from deck"),
     and it is written on the button BEFORE the player commits — which is the
     whole point: no tap should ever have two possible outcomes. */
  function ensureActionButton() {
    let button = document.getElementById('touch-preview-action');
    if (!button) {
      button = document.createElement('button');
      button.id = 'touch-preview-action';
      button.className = 'touch-preview-action';
      button.type = 'button';
      button.hidden = true;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        const pending = latchedAction;
        const target = latched?.el || null;
        unlatch();
        // The latch armed a one-shot swallow on the card so the enlarging tap
        // could not also play it. That swallow would eat the action's own click
        // — the one the player explicitly asked for.
        disarmSwallow();
        if (pending && typeof pending.run === 'function' && target) pending.run(target);
        // ...and the button's own trailing click must not repeat it.
        suppressNextClickAnywhere();
      });
      document.body.appendChild(button);
    }
    return button;
  }

  function resolveAction(source) {
    if (!source) return null;
    const resolved = typeof source === 'function' ? source() : source;
    if (!resolved || typeof resolved.run !== 'function') return null;
    return {
      label: String(resolved.label || ''),
      run: resolved.run,
      /* The move this control runs has a sound of its own, so it must not
         ALSO tick like a menu button. (The battle hand's Summon/Cast control
         was the one declarer; cards are played by dragging since 2026-09-24,
         and no surface declares it today.) */
      ownsPressCue: resolved.ownsPressCue === true,
    };
  }

  function showActionFor(el, source, options) {
    const resolved = resolveAction(source);
    latchedAction = resolved;
    const button = ensureActionButton();
    if (!resolved) {
      button.hidden = true;
      return;
    }
    button.textContent = resolved.label;
    /* The shared press cue (press-sound.js) ticks every <button>, this one
       included. `data-press-sound="off"` means the control owns its press
       feedback when the MOVE owns it: a move with a cue of its own sounds as
       that cue, not as a tick under it ("card summoning already had sounds",
       from when the battle hand had a Summon control). Written per latch,
       because the same control serves the deck builder's Add-to-deck, which
       has no cue of its own and keeps its tick. */
    if (resolved.ownsPressCue) button.dataset.pressSound = 'off';
    else delete button.dataset.pressSound;
    /* Revealed by the tracker once the anchor settles, never mid-animation —
       but a REPOSITION of a control that is already up must not hide it again.
       Blinking it off and on for every follow-up move is the same visual noise
       the settle gate exists to prevent. */
    if (!options?.keepVisible && button.hidden !== false) button.hidden = true;
    /* "Summon, button" does not say WHAT is being summoned, and the control is
       not inside the card it acts on, so nothing else supplies that context.
       Name the card in the accessible label where the surface exposes one. */
    const cardName = (el?.querySelector?.('.card-name')?.textContent || '').trim();
    if (cardName) button.setAttribute('aria-label', `${resolved.label} — ${cardName}`);
    else button.removeAttribute('aria-label');
    const anchor = actionAnchorFor(el);
    positionDiscoveryHint(button, anchor);
  }

  /**
   * Keep the control anchored to the card for as long as the latch is open.
   *
   * Settle-and-stop does not work here, in two separate ways that each shipped
   * a control sitting on top of the card. A popup surface mounts BEFORE the
   * card inside it exists, so early placements are computed against the wide
   * container; and the popup keeps being repositioned after its enlargement
   * transition appears to have finished — one measured run placed the control
   * correctly beside a card at x=315 and the card then slid to x=351, putting
   * it back underneath. Any fixed budget is a guess about someone else's
   * animation.
   *
   * So track instead: one rAF while the latch is open, repositioning only when
   * the anchor's identity or box actually changes. The latch is a short,
   * user-initiated state that always ends, so this cannot outlive the gesture.
   */
  function trackAnchorWhileLatched(el, action) {
    let previousKind = '';
    let previousBox = null;
    let stable = 0;
    let revealed = false;
    const openedAt = Date.now();
    // When the current anchor took over. The reveal deadline is measured from
    // here so the enlargement gets its own settle window when it lands.
    let anchorSince = openedAt;

    /* Before the control is up, ANY change counts as movement — that is what
       keeps it from being placed against a half-grown card. Once it is up, only
       a move the player would actually notice does, so idle jitter cannot make
       it dance. */
    const movedEnough = (box) => {
      if (!previousBox) return true;
      const budget = revealed ? ACTION_REANCHOR_THRESHOLD_PX : 0;
      return Math.abs(box.left - previousBox.left) > budget
        || Math.abs(box.top - previousBox.top) > budget
        || Math.abs(box.width - previousBox.width) > budget
        || Math.abs(box.height - previousBox.height) > budget;
    };

    const step = () => {
      if (!latched || latched.el !== el || !latchedAction) return;
      /* THE CARD IS BEING MAGNIFIED. Its box is the pinch's, not the layout's:
         re-seating the control against it would move the control while the
         player pinches ("elements should not move or reposition in any way"),
         and again as the popup springs back. Hold the seat; resume at rest. */
      if (magnifierHoldsTheCard()) {
        requestAnimationFrame(step);
        return;
      }
      const anchor = actionAnchorFor(el);
      const rect = anchor?.getBoundingClientRect?.();
      const kind = !rect ? ''
        : anchor === el ? 'src'
          : anchor.classList?.contains?.('card') ? 'card' : 'surface';
      const box = rect ? {
        left: Math.round(rect.left), top: Math.round(rect.top),
        width: Math.round(rect.width), height: Math.round(rect.height),
      } : null;

      // A different anchor is always a real move, however small the box change.
      const changed = !!box && (kind !== previousKind || movedEnough(box));
      stable = box && !changed ? stable + 1 : 0;
      if (changed) {
        if (kind !== previousKind) anchorSince = Date.now();
        previousKind = kind;
        previousBox = box;
        showActionFor(el, action, { keepVisible: revealed });
      }

      /* Placed correctly, but only REVEALED once the card has stopped moving —
         "the tap to replace and summon buttons dance around while the card is
         enlarging". Stay hidden through the animation, appear where it lands.
         The deadline is the backstop for a card whose animation never ends:
         late and correct beats never.

         And only once the seat is the FINAL one. `kind === 'src'` means the
         surface has not mounted its enlargement yet, so the control would be
         placed against the small source card and would move when the popup
         lands — see ENLARGEMENT_GRACE_MS. Showing a control the player cannot
         successfully press is worse than showing it a moment later. */
      /* `'card'`, not merely "not 'src'". A popup surface mounts BEFORE the
         card inside it, and the surface is a positioning container far larger
         than that card — so accepting the surface reseats the control the
         moment the card lands, which is the same teleport under a different
         name. Firefox reached exactly that state. */
      const seatIsFinal = previousKind === 'card'
        || Date.now() - openedAt >= ENLARGEMENT_GRACE_MS;
      const button = document.getElementById('touch-preview-action');
      if (button && !revealed && seatIsFinal
          && (stable >= ANCHOR_SETTLE_FRAMES
            || Date.now() - anchorSince >= ACTION_REVEAL_DEADLINE_MS)) {
        revealed = true;
        button.hidden = false;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function hideAction() {
    latchedAction = null;
    const button = document.getElementById('touch-preview-action');
    if (button) button.hidden = true;
  }

  function ensureDiscoveryHint(declared) {
    let hint = document.getElementById('touch-preview-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.id = 'touch-preview-hint';
      hint.className = 'touch-preview-hint';
      hint.setAttribute('role', 'status');
      hint.setAttribute('aria-live', 'polite');
      document.body.appendChild(hint);
    }
    /* The non-obvious half of the gesture is what the SECOND tap does — the
       first one taught itself the moment the card grew. Only the surface knows
       what its second tap does, so only the surface may word this. */
    hint.dataset.i18n = declared.key;
    hint.textContent = latchedHintText(declared);
    return hint;
  }

  function positionDiscoveryHint(hint, el) {
    const rect = el?.getBoundingClientRect?.();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!rect || !viewportWidth || !viewportHeight || (!rect.width && !rect.height)) return;
    const pad = 8;
    const hintWidth = hint.offsetWidth || 150;
    const hintHeight = hint.offsetHeight || 36;
    const safeInset = (name) => {
      try {
        const raw = window.getComputedStyle(document.documentElement).getPropertyValue(name);
        const value = Number.parseFloat(raw);
        return Number.isFinite(value) ? Math.max(0, value) : 0;
      } catch (_) {
        return 0;
      }
    };
    const safeTop = Math.max(pad, safeInset('--mf-safe-top'));
    const safeRight = Math.max(pad, safeInset('--mf-safe-right'));
    const safeBottom = Math.max(pad, safeInset('--mf-safe-bottom'));
    const safeLeft = Math.max(pad, safeInset('--mf-safe-left'));
    const maxLeft = Math.max(safeLeft, viewportWidth - hintWidth - safeRight);
    const maxTop = Math.max(safeTop, viewportHeight - hintHeight - safeBottom);
    const clampX = (value) => Math.max(safeLeft, Math.min(value, maxLeft));
    const clampY = (value) => Math.max(safeTop, Math.min(value, maxTop));
    const centredX = clampX(rect.left + rect.width / 2 - hintWidth / 2);
    const centredY = clampY(rect.top + rect.height / 2 - hintHeight / 2);

    /* Try each side, and take the first placement that does not land ON the
       card. Clamping a single preferred position was not enough: on a 288pt
       landscape page box an enlarged hand card is nearly the full height, so
       "below" clamped straight back over the card and the action control
       covered the very card the player was reading in order to decide.

       RIGHT comes first, then left, then the vertical placements. The owner
       asked for exactly this: "tap again to discard element overlaps card would
       be better to the right". A vertical placement clamps back ONTO a
       near-full-height enlarged card on a 288pt page box, which is the overlap
       being reported; a side placement has the room. The overlap check below
       still demotes any candidate that would land on the card, so a narrow
       viewport with no horizontal room falls through to above/below rather than
       printing the hint over the art. */
    const candidates = [
      { left: clampX(rect.right + pad), top: centredY, below: false },
      { left: clampX(rect.left - hintWidth - pad), top: centredY, below: false },
      { left: centredX, top: clampY(rect.top - hintHeight - pad), below: false },
      { left: centredX, top: clampY(rect.bottom + pad), below: true },
    ];
    const overlapArea = (candidate) => {
      const overlapX = Math.max(0, Math.min(candidate.left + hintWidth, rect.right)
        - Math.max(candidate.left, rect.left));
      const overlapY = Math.max(0, Math.min(candidate.top + hintHeight, rect.bottom)
        - Math.max(candidate.top, rect.top));
      return overlapX * overlapY;
    };
    const chosen = candidates.find((candidate) => overlapArea(candidate) === 0)
      // Nothing clears it — take the least-covering placement rather than
      // whichever one happened to be first.
      || candidates.slice().sort((a, b) => overlapArea(a) - overlapArea(b))[0];

    hint.style.left = `${Math.round(chosen.left)}px`;
    hint.style.top = `${Math.round(chosen.top)}px`;
    hint.classList.toggle('below', !!chosen.below);
  }

  /**
   * Two different things wear the same hint.
   *
   * Most surfaces use it to TEACH the second half of the gesture, and it
   * rightly retires the first time a second tap acts — `inspectionDiscovered`
   * is a session-wide flag, so once the player has done it anywhere, they know.
   *
   * The mulligan uses it as an AFFORDANCE: its second tap discards a card, and
   * "tap again to discard" is the only thing on screen that says so. Retiring
   * that after the first card meant it appeared on card one of the session and
   * never again — reported as the indicator showing up only sometimes. A
   * consequence the player must be told about is not a tutorial, so a surface
   * can declare its hint persistent and keep it.
   */
  function showDiscoveryHint(el, declared) {
    if (!declared?.key) return;
    if (inspectionDiscovered && declared.persistent !== true) return;
    const hint = ensureDiscoveryHint(declared);
    hint.dataset.anchorId = el.id || '';
    positionDiscoveryHint(hint, el);
    hint.classList.add('visible');
  }

  function hideDiscoveryHint() {
    const hint = document.getElementById('touch-preview-hint');
    if (!hint) return;
    hint.classList.remove('visible', 'below');
    hint.dataset.anchorId = '';
  }

  /** The card a press lands on: the one under the finger, else the one the
      attached element shows, else the element itself. */
  function ackTargetFor(el, target) {
    const underFinger = target?.closest?.('.card');
    if (underFinger && (underFinger === el || el.contains(underFinger))) return underFinger;
    if (el.matches?.('.card')) return el;
    return el.querySelector?.('.card') || el;
  }

  function areaOf(node) {
    const rect = node?.getBoundingClientRect?.();
    return rect ? rect.width * rect.height : 0;
  }

  function rendersVisibly(node) {
    if (!node?.isConnected) return false;
    let style;
    try { style = window.getComputedStyle(node); } catch (_) { return false; }
    if (style.display === 'none' || style.visibility !== 'visible') return false;
    if (style.opacity !== '' && Number(style.opacity) <= 0) return false;
    return areaOf(node) > 0;
  }

  /** Every card an enlargement surface is showing right now. */
  function previewCardsShown() {
    const shown = new Set();
    document.querySelectorAll(PREVIEW_SURFACES).forEach((surface) => {
      if (!rendersVisibly(surface)) return;
      const cards = surface.matches('.card') ? [surface] : [...surface.querySelectorAll('.card')];
      cards.forEach((card) => { if (rendersVisibly(card)) shown.add(card); });
    });
    return shown;
  }

  function startPressAck(el, event) {
    clearPressAck();
    const hold = TouchPress?.holdPending;
    if (typeof hold !== 'function') return;
    const target = ackTargetFor(el, event.target);
    pendingAck = {
      el,
      target,
      pointerId: event.pointerId,
      phase: 'pressing',
      release: hold(target),
      area: areaOf(target),
      startedAt: Date.now(),
      stale: null,
      expanded: false,
      frame: 0,
    };
  }

  function clearPressAck() {
    const ack = pendingAck;
    if (!ack) return;
    pendingAck = null;
    if (ack.frame) cancelAnimationFrame(ack.frame);
    ack.release();
  }

  /** The finger-down half only: an answer already being waited for stays. */
  function clearPressingAck(pointerId) {
    if (pendingAck?.phase !== 'pressing') return;
    if (pointerId !== undefined && pendingAck.pointerId !== pointerId) return;
    clearPressAck();
  }

  /* At the tap's release and BEFORE the latch runs the surface, so any
     enlargement already on screen is recognised as an earlier card's (one
     fading out after its dismissal must not answer this tap). */
  function awaitPressAnswer(el) {
    const ack = pendingAck;
    if (!ack || ack.el !== el) return;
    ack.phase = 'awaiting';
    ack.stale = previewCardsShown();
    ack.expanded = el.getAttribute('aria-expanded') === 'true';
  }

  function pressAnswered(ack) {
    if (!ack.target.isConnected || !ack.el.isConnected) return true;
    if (Date.now() - ack.startedAt >= PRESS_ACK_CAP_MS) return true;
    if (!ack.expanded && ack.el.getAttribute('aria-expanded') === 'true') return true;
    if (ack.area > 0 && areaOf(ack.target) > ack.area * IN_PLACE_GROWTH_AREA) return true;
    for (const card of previewCardsShown()) {
      if (!ack.stale.has(card)) return true;
    }
    return false;
  }

  /* Checked once in the tap's own task (a surface that answers at once shows
     no ring at all), then every frame, before it paints: the mark goes in the
     frame the enlargement arrives in. */
  function watchPressAnswer() {
    const ack = pendingAck;
    if (!ack || ack.phase !== 'awaiting') return;
    const step = () => {
      if (pendingAck !== ack) return;
      ack.frame = 0;
      if (pressAnswered(ack)) {
        clearPressAck();
        return;
      }
      let inline = true;
      ack.frame = requestAnimationFrame(() => {
        // A stand-in requestAnimationFrame that answers inside the call would
        // recurse without end; wait a frame's length instead.
        if (inline) setTimeout(step, 16);
        else step();
      });
      inline = false;
    };
    step();
  }

  function isSuppressingMouse() {
    return touchPointers.size > 0 || Date.now() < suppressUntil;
  }

  /** Read-only identity check for a surface that needs exact latch semantics. */
  function isLatched(el) {
    return !!el && latched?.el === el;
  }

  function dispatchSynthetic(el, type) {
    el.dispatchEvent(new MouseEvent(type, { bubbles: true }));
  }

  function disarmSwallow() {
    if (!armedSwallow) return;
    if (armedSwallow.timer) clearTimeout(armedSwallow.timer);
    armedSwallow.el.removeEventListener('click', armedSwallow.fn, true);
    armedSwallow = null;
  }

  function suppressNextClick(el) {
    disarmSwallow();
    /* Registered on DOCUMENT, in the capture phase, and scoped back to `el` by
       target test.

       Two reasons it cannot live on the element itself. `stopPropagation` there
       leaves the remaining listeners on that same node running, and the
       surfaces that latch bind their own click handler to exactly that node —
       which is why one tap on a mulligan card both enlarged it AND selected it.
       And `stopImmediatePropagation` there is still too late for any listener
       the surface registered EARLIER in the same phase. Capturing at the
       document runs before every listener on the target, whichever phase and
       whatever order they were bound in. */
    const swallow = (event) => {
      const target = event.target;
      /* ...and the enlargement counts as the source.
         The latch mounts the popup OVER the finger, so the browser retargets
         the tap's own compatibility click from the card to the popup — which
         every catalog binds its act-on-this-card handler to. Scoping this to
         `el` alone meant one deck-builder tap both enlarged a card AND added
         it, leaving the labelled control reading "Remove from deck"; pressing
         it took the card back out, so adding by touch never worked at all. */
      const onSource = target === el || !!(el.contains && el.contains(target));
      if (!onSource && !insidePreviewSurface(target)) return;
      disarmSwallow();
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    };
    armedSwallow = { el: document, fn: swallow, timer: null };
    document.addEventListener('click', swallow, true);
    /* This was the ONE arming path with no expiry, and an element-scoped
       swallow is the one that lingers longest: it only disarms when a click
       lands on `el` (or the preview surface). If the tap's own compatibility
       click never arrives — the surface got dismissed another way, the pointer
       cancelled, the finger glided off — the latch sat armed on that element
       indefinitely and ate its NEXT activation. On WebKit that killed keyboard
       and assistive activation of a story tile after a single tap: Enter fires
       a synthetic click, the stale latch swallowed it, and nothing opened. */
    scheduleSwallowExpiry();
  }

  /**
   * Swallow the next click WHEREVER it lands, once.
   *
   * The popup-tap path performs the action itself by dispatching a click on the
   * source card. The browser's own trailing click from that same tap then
   * arrives at whatever is under the finger once the popup has closed — usually
   * the source card — and fires the action a SECOND time. On a toggle (add to /
   * remove from deck) the two cancel out and the card never goes in, which is
   * exactly how "tapping marks a card as selected but doesn't add it" felt.
   * Element-scoped suppression cannot help here because the retarget is the
   * whole problem, so this one listens on document.
   */
  function suppressNextClickAnywhere() {
    disarmSwallow();
    const swallow = (event) => {
      disarmSwallow();
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    };
    armedSwallow = { el: document, fn: swallow, timer: null };
    document.addEventListener('click', swallow, true);
    scheduleSwallowExpiry();
  }

  /**
   * Swallow the next click ONLY if it lands on something playable, once.
   *
   * The tap-away path needs this rather than an element-scoped swallow: a
   * dismissal reflows the surface, so the element under the finger at pointerup
   * is not necessarily the element the trailing compatibility click hits. The
   * cost of a stray play is a move the player cannot take back; the cost of a
   * stray chrome click is nothing, so chrome is deliberately let through and
   * stays responsive on the first tap.
   */
  function suppressNextClickOnPlayableSurface() {
    disarmSwallow();
    const swallow = (event) => {
      const target = event.target;
      if (typeof target?.closest !== 'function' || !target.closest(PLAYABLE_SURFACES)) return;
      disarmSwallow();
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    };
    armedSwallow = { el: document, fn: swallow, timer: null };
    document.addEventListener('click', swallow, true);
    scheduleSwallowExpiry();
  }

  /**
   * The gesture (or a touch) just ended: if the trailing click never arrives
   * (pointercancel, glide-away lift, Android long-press), the swallow must
   * not linger and eat the NEXT legitimate tap — expire it shortly after.
   */
  function scheduleSwallowExpiry() {
    if (!armedSwallow) return;
    if (armedSwallow.timer) clearTimeout(armedSwallow.timer);
    armedSwallow.timer = setTimeout(disarmSwallow, MOUSE_SUPPRESS_MS);
  }

  /**
   * Drop the latched preview, if any: tell the surface to close and re-arm the
   * compatibility-mouse grace. Also the external cross-cancellation entry
   * point, exported as cancelActivePress (CardDrag.beginDrag).
   */
  /**
   * @param {{ pointerId?: number, quiet?: boolean }} [options] `quiet` drops the latch WITHOUT
   *   running the surface's teardown. Used when the drag system is taking the
   *   gesture: a surface's onDismiss can re-render or re-raster the very card
   *   being dragged, and CardDrag aborts with `source-removed-before-threshold`
   *   if its source detaches before the drag begins — which is a drag that
   *   simply never happens. CardDrag clears the shared popup itself in
   *   beginDrag, so nothing is left on screen by skipping it here.
   */
  function unlatch(options) {
    cancelPress();
    // A press still under the finger is abandoned with the gesture (the drag
    // system taking it, a cancel); an answer being waited for goes with its
    // own latch, never with the one this tap is replacing.
    if (pendingAck && (pendingAck.phase === 'pressing' || pendingAck.el === latched?.el)) clearPressAck();
    if (!latched) return;
    const { el, callbacks } = latched;
    latched = null;
    hideDiscoveryHint();
    hideAction();
    scheduleSwallowExpiry();
    const dismissSurface = () => {
      if (callbacks) {
        if (typeof callbacks.onDismiss === 'function') callbacks.onDismiss(el);
      } else {
        dispatchSynthetic(el, 'mouseleave');
      }
    };
    if (options && options.quiet) {
      /* TouchPreview's document-capture listener runs before CardDrag's. Drop
         the latch immediately so CardDrag can lift an undisturbed source, then
         decide after the current event has reached every listener. Raster
         preparation may retain CardDrag's tracked pointer before a clone
         exists, so keep the teardown pending until that gesture either starts
         or is actually refused/released. */
      flushPendingDragDismissal();
      pendingDragDismissal = {
        dismissSurface,
        pointerId: options.pointerId,
        scheduled: false,
      };
      scheduleDragHandoffDecision(options.pointerId);
      return;
    }
    dismissSurface();
  }

  /** Abandon a finger-down without touching the latch. */
  function cancelPress() {
    press = null;
  }

  /** Open (or re-target) the latched preview and swallow the tap's own click. */
  function latchOnto(el, callbacks, action, hint) {
    if (latched && latched.el !== el) unlatch();
    if (latched && latched.el === el) return;
    latched = { el, callbacks, action };
    if (action) {
      // A declared action replaces the hint: the control IS the instruction,
      // and it says exactly what pressing it will do.
      showActionFor(el, action);
      hideDiscoveryHint();
      trackAnchorWhileLatched(el, action);
    } else if (hint?.key) {
      /* Teach the second half of the gesture until the player uses it — the hint
         retires the first time a second tap acts on a card. Surfaces that do not
         declare one are inspect-only and stay silent. */
      showDiscoveryHint(el, hint);
    } else {
      hideDiscoveryHint();
    }
    if (callbacks) {
      if (typeof callbacks.onPreview === 'function') callbacks.onPreview(el);
    } else {
      dispatchSynthetic(el, 'mouseenter');
    }
    suppressNextClick(el);
  }

  function dragSystemOwnsGesture() {
    try {
      return !!(CardDrag
        && typeof CardDrag.isDragging === 'function'
        && CardDrag.isDragging());
    } catch (_) {
      return false;
    }
  }

  function dragSystemTracksPointer(pointerId) {
    try {
      return !!CardDrag?.ownsPointer?.(pointerId);
    } catch (_) {
      return false;
    }
  }

  function dragSystemPointerState(pointerId) {
    if (!dragSystemTracksPointer(pointerId)) return 'idle';
    return dragSystemOwnsGesture() ? 'dragging' : 'tracking';
  }

  function flushPendingDragDismissal() {
    const pending = pendingDragDismissal;
    pendingDragDismissal = null;
    pending?.dismissSurface?.();
  }

  function scheduleDragHandoffDecision(pointerId) {
    const pending = pendingDragDismissal;
    if (!pending || pending.pointerId !== pointerId || pending.scheduled) return;
    if (dragSystemPointerState(pointerId) === 'dragging') {
      pendingDragDismissal = null;
      return;
    }
    pending.scheduled = true;
    const decide = () => {
      if (pendingDragDismissal !== pending) return;
      pending.scheduled = false;
      const state = dragSystemPointerState(pointerId);
      if (state === 'dragging') {
        pendingDragDismissal = null;
      } else if (state === 'idle') {
        flushPendingDragDismissal();
      }
    };
    if (typeof queueMicrotask === 'function') queueMicrotask(decide);
    else Promise.resolve().then(decide);
  }

  function settleDragHandoff(pointerId, accepted) {
    const pending = pendingDragDismissal;
    if (!pending || pending.pointerId !== pointerId) return;
    pendingDragDismissal = null;
    if (!accepted) pending.dismissSurface();
  }

  // Under the mobile-first UI, arm for a fine pointer too — a DevTools device
  // emulator reports pointerType 'mouse', and a phone stand-in should behave
  // like a phone. A real desktop pointer never reaches this module, so hover
  // keeps working exactly as before.
  function mobileUiActive() {
    try {
      return !!(Platform && Platform.isMobileUi && Platform.isMobileUi());
    } catch (_) {
      return false;
    }
  }

  function insidePreviewSurface(node) {
    try {
      return !!(node && node.closest && node.closest(PREVIEW_SURFACES));
    } catch (_) {
      return false;
    }
  }

  /** zoom-policy.js is holding the enlarged card under a pinch, or easing it back. */
  function magnifierHoldsTheCard() {
    try {
      return !!window.MoonfallZoomPolicy?.isMagnifying?.();
    } catch (_) {
      return false;
    }
  }

  function onElementPointerDown(el, event, callbacks, action, shouldPreview, hint) {
    const isTouch = event.pointerType === 'touch';
    if (!isTouch && !mobileUiActive()) return;
    if (isTouch) {
      touchPointers.add(event.pointerId);
      // Inspection is intentionally a single-touch gesture. A second finger
      // abandons the tap — but NOT the latch. That finger is a pinch, the
      // pinch magnifies the enlarged card (zoom-policy.js), and dropping the
      // latch here used to close the very card being read. Tap-away still
      // closes.
      if (touchPointers.size > 1) {
        pinching = true;
        cancelPress();
        clearPressingAck();
        return;
      }
    }

    /* Some toggle surfaces have a state whose next tap is already
       unambiguous. A selected mulligan card, for example, must deselect on the
       very next tap instead of opening a no-op preview and requiring a fourth
       gesture. Evaluate this at pointerdown so the predicate can follow live
       state. Predicate failure keeps inspection enabled, which is the safer
       outcome than accidentally committing an action. */
    if (typeof shouldPreview === 'function') {
      let enabled = true;
      try { enabled = shouldPreview(el) !== false; } catch (_) { enabled = true; }
      if (!enabled) return;
    }

    /* The INNERMOST attached element owns the tap. A board slot and the card in
       it are both attached, and a pointerdown reaches the card's handler first,
       then the slot's as it bubbles — so the ANCESTOR ended up holding the
       press, the latch opened on the slot, and because the slot's preview path
       is mouse-only and guarded a tapped board card never enlarged.
       This swap previously broke dragging, and the reason was the dismissal
       rather than the ownership: with the press on the card, handing the
       gesture to the drag system ran that card's onDismiss, which disturbed the
       source, and CardDrag aborts when its source detaches before the
       threshold. The handover is quiet now, so both work. */
    if (press
      && press.pointerId === event.pointerId
      && press.el !== el
      && typeof el.contains === 'function'
      && el.contains(press.el)) {
      return;
    }

    /* Superseded note, kept for the history it explains:
       A board slot and the card in it are both attached, and a pointerdown
       reaches the card's handler first, then the slot's as it bubbles, so the
       ANCESTOR ends up holding the press. That is why a tapped board card does
       not enlarge: the latch opens on the slot, whose preview path is
       mouse-only and guarded.
       Handing the press to the card instead FIXES the enlargement and BREAKS
       dragging — proven by A/B on the real backend, drag passing with the
       ancestor and failing with the card, everything else held constant. A
       board card is both a drag source and a drop target, so something in that
       handover depends on which element TouchPreview is holding. Enlargement
       is worth having; a working drag is worth more, so this stays as it is
       until the interaction between the two is understood rather than traded.
       Tracked in docs/mobile-page-box.md. */

    press = {
      el,
      callbacks,
      action,
      hint,
      pointerId: event.pointerId,
      startX: event.clientX || 0,
      startY: event.clientY || 0,
      moved: false,
    };
    // The second tap on the enlarged card acts (or dismisses) at once; only a
    // tap that has an enlargement to wait for needs answering first.
    if (!(latched && latched.el === el)) startPressAck(el, event);
  }

  function onDocumentPointerDown(event) {
    scheduleDragHandoffDecision(event.pointerId);
    const isTouch = event.pointerType === 'touch';
    if (!isTouch && !mobileUiActive()) return;
    if (isTouch) {
      /* iOS may abandon a pointer stream when browser chrome, magnification,
         an interruption, or app backgrounding takes ownership. A subsequent
         real first finger isPrimary=true; clear only then. A genuine second
         finger isPrimary=false and must retain the multi-touch cancellation
         path below. */
      if (event.isPrimary === true && touchPointers.size > 0) {
        touchPointers.clear();
        pinching = false;
        cancelPress();
        clearPressingAck();
      }
      touchPointers.add(event.pointerId);
      /* Seen here first (capture), before the element handler: from the
         second finger on, nothing in this gesture is a tap. The press is
         dropped now so the first finger's later movement cannot be mistaken
         for a drag handoff, which would dismiss the latch "quietly". */
      if (touchPointers.size > 1) {
        pinching = true;
        cancelPress();
        clearPressingAck();
      }
    }
    if (insideActionControl(event.target)) return;
    // A NEW gesture starting while a stale swallow is armed means the swallowed
    // click never came (pointercancel / no-click lift) — disarm so this tap
    // plays through. A live latch keeps its swallow.
    if (armedSwallow && !latched) disarmSwallow();
  }

  function onDocumentPointerMove(event) {
    scheduleDragHandoffDecision(event.pointerId);
    if (!press || press.moved) return;
    if (event.pointerId !== press.pointerId) return;
    const dx = (event.clientX || 0) - press.startX;
    const dy = (event.clientY || 0) - press.startY;
    if (Math.sqrt(dx * dx + dy * dy) >= slopPx()) {
      // The drag system owns this gesture now; a drag also dismisses — but
      // QUIETLY, or the surface's teardown re-renders the card out from under
      // the drag that is just starting.
      press.moved = true;
      cancelPress();
      clearPressingAck(event.pointerId);
      unlatch({ pointerId: event.pointerId, quiet: true });
    }
  }

  /**
   * The tap resolves here, on release — which is what makes this a latch and
   * not a hold. Everything the gesture decides is decided from: was there a
   * latch, and where did this tap land relative to it.
   */
  function onDocumentPointerEnd(event) {
    scheduleDragHandoffDecision(event.pointerId);
    const isTouch = event.pointerType === 'touch';
    if (!isTouch && !mobileUiActive()) return;
    const wasPinching = pinching;
    if (isTouch) {
      touchPointers.delete(event.pointerId);
      if (touchPointers.size === 0) {
        // Only the LAST lifting finger starts the compatibility-mouse grace.
        suppressUntil = Date.now() + MOUSE_SUPPRESS_MS;
        pinching = false;
      }
    }

    if (insideActionControl(event.target)) return;

    /* A finger lifting out of a pinch is not a tap. It must not act — a
       release over the enlarged popup is otherwise "the second tap", and a
       pinch on the card would PLAY it — and it must not dismiss: an engine
       may send pointercancel for both fingers mid-pinch, and that is the
       magnifier at work, not the player leaving. The popup springs back to
       rest on its own (zoom-policy.js). */
    if (wasPinching) {
      cancelPress();
      clearPressingAck();
      return;
    }

    const tap = press && event.pointerId === press.pointerId && !press.moved ? press : null;
    cancelPress();

    if (event.type === 'pointercancel') {
      clearPressingAck(event.pointerId);
      scheduleSwallowExpiry();
      unlatch();
      return;
    }

    if (tap) {
      // A re-render can detach the card between press and release, and CardDrag
      // may have taken the gesture under our slop — neither should enlarge.
      if (!tap.el.isConnected || dragSystemOwnsGesture()) {
        clearPressingAck(event.pointerId);
        scheduleSwallowExpiry();
        return;
      }
      if (latched && latched.el === tap.el) {
        clearPressingAck(event.pointerId);
        if (latched.action) {
          // Acting has its own control, so a second tap only dismisses — and
          // the click is swallowed so dismissing can never also play the card.
          unlatch();
          suppressNextClick(tap.el);
          return;
        }
        // No declared action on this surface: keep the two-tap behaviour, and
        // deliberately do NOT swallow — the trailing click is what acts.
        inspectionDiscovered = true;
        unlatch();
        disarmSwallow();
        return;
      }
      awaitPressAnswer(tap.el);
      latchOnto(tap.el, tap.callbacks, tap.action, tap.hint);
      watchPressAnswer();
      return;
    }

    // A release that is not this press's tap (it moved, or it is another
    // pointer's) answers nothing.
    clearPressingAck(event.pointerId);

    if (!latched) {
      scheduleSwallowExpiry();
      return;
    }

    if (insidePreviewSurface(event.target)) {
      // The popup covers its own source, so a tap on it is the second tap.
      const { el } = latched;
      if (latchedAction) {
        /* ONE way to play, everywhere: when a surface declares an action, the
           labelled control is the only thing that acts — same as a second tap
           on the card itself. Dispatching the click here made the battle hand
           a two-system hybrid ("tap to enlarge then tap to summon") while the
           mulligan was button-only. */
        unlatch();
        suppressNextClick(el);
        return;
      }
      inspectionDiscovered = true;
      unlatch();
      // The first tap's swallow is still armed on the source; it would eat the
      // very click we are about to send.
      disarmSwallow();
      if (el.isConnected) el.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      // The action has now happened exactly once. The tap's own trailing click
      // is still to come and would repeat it.
      suppressNextClickAnywhere();
      return;
    }

    /* Tap-away: dismiss. Whether that tap ALSO does its normal job depends on
       what it landed on.
       Swallowing everything was wrong. A preview latches on every card tap, so
       in practice a preview is almost always open — and the first tap on Return
       to Lobby, on a modal's X, or on a mulligan card did nothing at all. Three
       separate "button doesn't work" reports, one cause.
       Swallow only where a stray press is genuinely costly: another CARD, where
       an accidental play is a move the player cannot take back. Chrome stays
       responsive on the first tap, which is what a finger expects. */
    /* Decided at CLICK time, not at pointerup.
       Scoping the swallow to whatever was under the finger when it lifted left
       a hole, because dismissing is itself a layout change: the enlarged card
       shrinks back and the grid reflows, so a `.deck-card-item` can slide under
       the finger in the milliseconds before WebKit dispatches the trailing
       compatibility click. Measured on a real iPhone page box — pointerdown and
       pointerup both hit the empty `#collection-cards-grid`, the click landed on
       a card, and tapping away from an enlarged card opened a card detail the
       player never asked for.
       Same policy, evaluated against the target that actually receives the
       click: a stray play is unaffordable, chrome stays responsive on the first
       tap. */
    unlatch();
    suppressNextClickOnPlayableSurface();
  }

  /**
   * Swallow the COMPATIBILITY mouse events a tap synthesizes.
   *
   * A mobile browser follows every tap with a trusted mouseover/mouseenter/
   * mousemove/mouseout/mouseleave sequence around the touch point. The leave
   * half lands a beat after this module has latched a preview open — and every
   * hover system in the app treats it as "the pointer left", tearing the
   * enlargement down again. That is why cards were unenlargeable on a phone
   * across the collection, the deck builder, Moon Dream, Moon Fuse and the
   * exchange: an audit found 22 mouseleave handlers and exactly one of them
   * guarded against it.
   *
   * Guarding 22 call sites would work until the 23rd is written, so the events
   * are stopped here instead, in the capture phase, before any of them run.
   * Only TRUSTED events are stopped, and only inside the suppression window —
   * the synthetic ones this module dispatches (isTrusted === false) are what
   * the previews are meant to react to, and they pass straight through.
   */
  const COMPATIBILITY_MOUSE_EVENTS = [
    'mouseover', 'mouseout', 'mouseenter', 'mouseleave', 'mousemove',
  ];
  function swallowCompatibilityMouse(event) {
    if (event.isTrusted !== true || !isSuppressingMouse()) return;
    event.stopPropagation();
  }
  COMPATIBILITY_MOUSE_EVENTS.forEach((type) => {
    document.addEventListener(type, swallowCompatibilityMouse, true);
  });

  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('pointermove', onDocumentPointerMove, true);
  document.addEventListener('pointerup', onDocumentPointerEnd, true);
  document.addEventListener('pointercancel', onDocumentPointerEnd, true);

  /**
   * A scroll retires the latch.
   *
   * The enlargement is anchored to a card, and every catalog that shows one
   * lives inside a scroller. Scrolling took the card away and left the popup
   * behind — an enlarged card with nothing under it, over a UI that could no
   * longer be reached past it. Reported on the Moon Fuse dropdown as "double
   * tap a card ... produce a perma zoom which breaks the ui".
   *
   * Capture phase, because scroll does not bubble from the element that
   * scrolled. Nothing here cancels the scroll itself.
   */
  /* Deliberately unconditional.
     A refinement that only retired the latch when the anchor had MOVED was
     tried and reverted: opening a preview re-lays-out its own surface, so the
     row legitimately shifts in the same frame, and Moon Fuse's inventory tap
     dismissed itself instantly in Firefox. The speculative case it guarded
     against — an iOS momentum frame arriving after the tap — was never
     observed, and the regression was. Any scroll retires the latch. */
  function onAnyScroll() {
    if (!latched) return;
    unlatch();
  }

  document.addEventListener('scroll', onAnyScroll, true);

  function resetInteraction() {
    touchPointers.clear();
    pinching = false;
    suppressUntil = 0;
    cancelPress();
    clearPressAck();
    flushPendingDragDismissal();
    unlatch();
    disarmSwallow();
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') resetInteraction();
  }

  window.addEventListener('blur', resetInteraction);
  window.addEventListener('pagehide', resetInteraction);
  document.addEventListener('visibilitychange', onVisibilityChange);

  /**
   * @param {Element} el
   * @param {{
   *   onPreview?: (el: Element) => void,
   *   onDismiss?: (el: Element) => void,
   *   shouldPreview?: (el: Element) => boolean,
   *   hint?: { key: string, fallback: string },
   * }} [options]
   *   When provided, the tap fires onPreview(el) instead of a synthetic
   *   mouseenter and dismissal fires onDismiss(el) instead of mouseleave.
   *   `hint` is the surface's own promise about what a SECOND tap does. It is
   *   opt-in on purpose: a surface whose second tap only dismisses (a board
   *   card that acts by dragging, a storyboard tile, a revealed enemy spell)
   *   must not display a promise it does not keep.
   */
  function attach(el, options) {
    if (!el) return;
    const callbacks = options
      && (typeof options.onPreview === 'function' || typeof options.onDismiss === 'function')
      ? { onPreview: options.onPreview, onDismiss: options.onDismiss }
      : null;
    const action = options && options.action ? options.action : null;
    const shouldPreview = options && typeof options.shouldPreview === 'function'
      ? options.shouldPreview
      : null;
    const hint = options && options.hint && options.hint.key ? options.hint : null;

    /* Several surfaces attach the SAME element more than once — a generic pass
       first, then a specific one carrying the callbacks or the action. Returning
       early on the repeat meant whichever call happened to run first silently
       won, and a surface's action control never appeared because a bare
       attach had already claimed the card. The listener is still registered once; the
       options it reads are refreshed, and a later call may only ADD to them. */
    const previous = attachedOptions.get(el);
    attachedOptions.set(el, {
      callbacks: callbacks || previous?.callbacks || null,
      action: action || previous?.action || null,
      shouldPreview: shouldPreview || previous?.shouldPreview || null,
      hint: hint || previous?.hint || null,
    });
    if (attached.has(el)) return;

    attached.add(el);
    describeInspection(el);
    el.addEventListener('pointerdown', (event) => {
      const current = attachedOptions.get(el) || {};
      onElementPointerDown(
        el,
        event,
        current.callbacks || null,
        current.action || null,
        current.shouldPreview || null,
        current.hint || null,
      );
    });
  }

  /** Detach every document listener and armed swallow this instance owns. */
  function destroy() {
    resetInteraction();
    document.removeEventListener('scroll', onAnyScroll, true);
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    document.removeEventListener('pointermove', onDocumentPointerMove, true);
    document.removeEventListener('pointerup', onDocumentPointerEnd, true);
    document.removeEventListener('pointercancel', onDocumentPointerEnd, true);
    COMPATIBILITY_MOUSE_EVENTS.forEach((type) => {
      document.removeEventListener(type, swallowCompatibilityMouse, true);
    });
    window.removeEventListener('blur', resetInteraction);
    window.removeEventListener('pagehide', resetInteraction);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  const instance = {
    attach,
    isLatched,
    /* The card whose enlargement is open. CardDrag hands a press that lands on
       that enlargement to this card, so dragging the card the player is
       reading never picks up whatever is painted beneath the popup. */
    latchedElement: () => latched?.el || null,
    isSuppressingMouse,
    cancelActivePress: unlatch,
    settleDragHandoff,
    destroy,
  };
  window.__moonfallTouchPreview = instance;
  TouchPreview = instance;
})();

export { TouchPreview };
